import * as i18n from "util/i18n";
import { actions as metrocorpActions } from "reducers/metrocorp";
import { actions as notificationActions } from "reducers/notification";

export const SCOPES = ["metrocorp.list"];
export const GO_BACK_PATH = "/v3/dashboard";
export const I18N_PREFIX = "metrocorp.operations";
export const EXTRACTS_FLAG = "frontend.show.metrocorpExtracts.functionalities";
export const EXTRACTS_CALENDAR_YEARS_KEY = "frontend.show.calendar.resumenes.Metrocorp";

export function getLabels(key) {
    return i18n.get(`${I18N_PREFIX}.${key}`);
}

export function parseIsoDate(iso) {
    if (!iso) {
        return null;
    }
    const parts = String(iso)
        .split("-")
        .map(Number);
    if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) {
        return null;
    }
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function extractHoldingsPayload(response) {
    return response?.data?.data ?? response?.data ?? {};
}

export function speciesAllOption(filters, allLabel = "Todas") {
    if (filters.selectedSpecies) {
        const selected = filters.selectedSpecies;
        const selectedId = selected.value ?? selected.id;
        const match = (filters.speciesOptions || []).find((option) => String(option.id) === String(selectedId));
        if (match) {
            return {
                value: match.id,
                label: match.id === "all" ? allLabel : match.label,
                searchKey: match.id === "all" ? allLabel : match.label,
            };
        }
        return {
            value: selectedId,
            label: selected.label || String(selectedId),
            searchKey: selected.label || String(selectedId),
        };
    }
    const fromOptions = (filters.speciesOptions || []).find((option) => option.id === "all");
    if (fromOptions) {
        return { value: fromOptions.id, label: allLabel, searchKey: allLabel };
    }
    return { value: "all", label: allLabel, searchKey: allLabel };
}

export function mapSpeciesOption(option, allSpeciesLabel) {
    return option.id === "all" ? { ...option, label: allSpeciesLabel } : option;
}

export function hasAccountId(accountId) {
    return accountId !== undefined && accountId !== null && accountId !== "";
}

export function accountOption(filters, accountId, verifyCode) {
    const match = (filters.accountOptions || []).find((option) => String(option.id) === String(accountId));
    if (match) {
        return {
            value: match.id,
            label: match.label,
            verifyCode: match.verifyCode || verifyCode,
        };
    }
    return { value: accountId, label: String(accountId), verifyCode };
}

export function resolveHoldingsInitialValues(filters, navFilters, allSpeciesLabel) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let account = filters.selectedAccount || null;
    if (navFilters) {
        if (navFilters.accountId === null) {
            account = null;
        } else if (hasAccountId(navFilters.accountId)) {
            account = accountOption(filters, navFilters.accountId, navFilters.verifyCode);
        }
    }
    return {
        account,
        species: navFilters ? speciesAllOption(filters, allSpeciesLabel) : filters.selectedSpecies || null,
        date: navFilters ? today : parseIsoDate(filters.date),
    };
}

export function toIsoDate(value) {
    if (!value) {
        return undefined;
    }
    if (typeof value === "string") {
        return value.length >= 10 ? value.slice(0, 10) : value;
    }
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return undefined;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function toFilterId(value) {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (typeof value === "string") {
        return value;
    }
    return value.value || value.id;
}

function startOfDay(date) {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
}

function addDays(date, days) {
    const next = startOfDay(date);
    next.setDate(next.getDate() + days);
    return next;
}

export function periodRange(period, customDates) {
    const today = startOfDay(new Date());
    if (period === "yesterday") {
        const yesterday = addDays(today, -1);
        return { dateFrom: toIsoDate(yesterday), dateTo: toIsoDate(yesterday) };
    }
    if (period === "lastWeek") {
        return { dateFrom: toIsoDate(addDays(today, -6)), dateTo: toIsoDate(today) };
    }
    if (period === "last30Days") {
        return { dateFrom: toIsoDate(addDays(today, -29)), dateTo: toIsoDate(today) };
    }
    if (period === "custom") {
        return {
            dateFrom: toIsoDate(customDates?.[0]) || toIsoDate(today),
            dateTo: toIsoDate(customDates?.[1]) || toIsoDate(today),
        };
    }
    return { dateFrom: toIsoDate(today), dateTo: toIsoDate(today) };
}

/**
 * Privacy for movement amounts is handled by FormattedAmount via `hideAmount`.
 * Do not replace `amount` with a placeholder: that broke movementAmountSign
 * (masked strings never start with "-", so debits rendered as "+ ARS ****").
 */
export function maskMovement(movement, hideAmount) {
    if (!hideAmount || !movement) {
        return movement;
    }
    return {
        ...movement,
        movementsGrouped: (movement.movementsGrouped || []).map((child) => maskMovement(child, hideAmount)),
    };
}

function verifyCodeForAccount(account, accountOptions) {
    if (account?.verifyCode) {
        return account.verifyCode;
    }
    const accountId = toFilterId(account);
    if (!accountId) {
        return undefined;
    }
    const match = (accountOptions || []).find((option) => String(option.id) === String(accountId));
    return match?.verifyCode;
}

export function holdingsRequestFromFilters(values, accountOptions) {
    const account = toFilterId(values?.account);
    const verifyCode = verifyCodeForAccount(values?.account, accountOptions);
    const species = toFilterId(values?.species);
    const date = toIsoDate(values?.date);
    return {
        ...(account ? { account } : {}),
        ...(verifyCode ? { verifyCode } : {}),
        ...(species ? { species } : {}),
        ...(date ? { date } : {}),
    };
}

const SETTLEMENT_CHART_LABELS = {
    "liq-24h": "futureValues.chart.liq24",
    "liq-48h": "futureValues.chart.liq48",
    "liq-future": "futureValues.chart.liqFuture",
};

const SETTLEMENT_ACCORDION_LABELS = {
    "liq-24h": "futureValues.group.liq24",
    "liq-48h": "futureValues.group.liq48",
    "liq-future": "futureValues.group.liqFuture",
};

export function mapSettlementSection(payload, hideAmount) {
    const settlement = payload?.settlement;
    if (!settlement) {
        return { showSettlementSection: false };
    }
    return {
        showSettlementSection: true,
        settlementHighlight: {
            termLabel: getLabels("futureValues.settlement.term"),
            horizonLabel: getLabels("futureValues.settlement.horizon"),
            amount: Number(settlement.t1Amount) || 0,
            currency: settlement.currency || "ARS",
            dueDateLabel: i18n.get(`${I18N_PREFIX}.futureValues.dueDate`, null, { date: settlement.dueDate }),
            hideAmount,
        },
        settlementChartTitle: getLabels("futureValues.settlementChartTitle"),
        settlementChartData: (settlement.bars || []).map((bar) => ({
            date: getLabels(SETTLEMENT_CHART_LABELS[bar.id] || bar.id),
            amount: Number(bar.amount) || 0,
            groupId: bar.id,
        })),
        settlementChartYAxisTicks: settlement.yAxisTicks,
        groups: (settlement.groups || []).map((group) => ({
            ...group,
            label: getLabels(SETTLEMENT_ACCORDION_LABELS[group.id] || group.id),
        })),
    };
}

export function holdingsRequestFromNavigation(navFilters) {
    if (!navFilters) {
        return {};
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return holdingsRequestFromFilters({
        account: navFilters.accountId ? { value: navFilters.accountId, verifyCode: navFilters.verifyCode } : null,
        species: navFilters.speciesId ? { value: navFilters.speciesId } : null,
        date: today,
    });
}

export function getNavigationFilters(location) {
    return location?.state?.metrocorpHoldingsFilters;
}

export function mapKpi(payload, { fetchingPerformance, shouldHideAmounts }) {
    if (!payload?.patrimony) {
        return undefined;
    }
    const dateLabel = payload.valuationDateLabel || "";
    return {
        patrimony: {
            ...payload.patrimony,
            accountLabel: getLabels("patrimonyAccountLabel"),
            asOfLabel: dateLabel ? i18n.get(`${I18N_PREFIX}.valuationDate`, null, { date: dateLabel }) : undefined,
            hideAmount: shouldHideAmounts,
        },
        hideAmount: shouldHideAmounts,
        isPerformanceLoading: fetchingPerformance,
        performanceTitle: i18n.get(`${I18N_PREFIX}.performanceTitle`, null, { date: dateLabel }),
        performanceChartData: payload.performanceChartData || [],
        portfolioTitle: getLabels("portfolioTitle"),
        portfolioAsOfLabel: dateLabel
            ? i18n.get(`${I18N_PREFIX}.portfolioAsOfLabel`, null, { date: dateLabel })
            : undefined,
        portfolioChartData: payload.portfolioChartData || [],
        portfolioLegend: payload.portfolioLegend || [],
        portfolioBreakdownTitle: getLabels("portfolioBreakdownTitle"),
        portfolioBreakdownItems: payload.portfolioBreakdownItems || {},
    };
}

export function resolvePerformanceVerifyCode(filters, account) {
    const { selectedAccount } = filters;
    const matchingAccountOption = (filters.accountOptions || []).find(
        (option) => String(option.id) === String(account),
    );
    return selectedAccount?.verifyCode || matchingAccountOption?.verifyCode;
}

export function buildHoldingsSection(holdings, options) {
    const kpi = mapKpi(holdings, options);
    if (!kpi) {
        return undefined;
    }
    const filters = holdings.holdingsFilters || {};
    const allSpeciesLabel = getLabels("holdings.filter.species.all");
    return {
        getLabels,
        ...kpi,
        onDownloadPerformance: options.onDownloadPerformance,
        categories: holdings.categories || [],
        holdingsFilters: {
            accountOptions: filters.accountOptions || [],
            speciesOptions: (filters.speciesOptions || []).map((option) => mapSpeciesOption(option, allSpeciesLabel)),
            initialValues: resolveHoldingsInitialValues(filters, options.navigationFilters, allSpeciesLabel),
            onSubmit: options.onFilterHoldings,
            submitOnAccountChange: true,
            filtersActive: options.holdingsFiltersActive,
            onClearFilters: options.onClearHoldingsFilters,
            resetVersion: options.holdingsResetVersion,
            loading: options.holdingsFiltering,
        },
    };
}

export function buildFutureValuesSection(futureValues, options) {
    const kpi = mapKpi(futureValues, options);
    if (!kpi) {
        return undefined;
    }
    const filters = futureValues.holdingsFilters || {};
    const allSpeciesLabel = getLabels("holdings.filter.species.all");
    return {
        getLabels,
        ...kpi,
        ...mapSettlementSection(futureValues, options.shouldHideAmounts),
        onDownloadPerformance: options.onDownloadFutureValuesPerformance,
        filters: {
            accountOptions: filters.accountOptions || [],
            speciesOptions: (filters.speciesOptions || []).map((option) => mapSpeciesOption(option, allSpeciesLabel)),
            initialValues: {
                account: filters.selectedAccount || null,
                species: speciesAllOption(filters, allSpeciesLabel),
            },
            onSubmit: options.onFilterFutureValues,
            submitOnAccountChange: true,
            filtersActive: options.futureValuesFiltersActive,
            onClearFilters: options.onClearFutureValuesFilters,
            resetVersion: options.futureValuesResetVersion,
            loading: options.futureValuesFiltering,
        },
    };
}

export function downloadPerformanceFrom(dispatch, payload, date) {
    const filters = payload?.holdingsFilters || {};
    const account = toFilterId(filters.selectedAccount);
    const verifyCode = resolvePerformanceVerifyCode(filters, account);
    if (!account) {
        return;
    }
    if (!verifyCode) {
        dispatch(
            notificationActions.showNotification(
                i18n.get("metrocorp.performance.verifyCode.invalid"),
                "error",
                SCOPES,
                null,
                false,
            ),
        );
        return;
    }
    dispatch(metrocorpActions.downloadMetrocorpPerformance(account, date, verifyCode));
}

export function mapExtractAccountOptions(accountOptions) {
    return (accountOptions || [])
        .map((option) => {
            const value = option?.id ?? option?.value;
            if (value === undefined || value === null || value === "") {
                return null;
            }
            return { value, label: option.label ?? String(value) };
        })
        .filter(Boolean);
}

export function extractAccountFromSubmit(account) {
    if (account == null) {
        return undefined;
    }
    if (typeof account === "object") {
        return account.value ?? account.id;
    }
    return account;
}

export function defaultExtractMonth(now = new Date()) {
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

export function extractMinDate(years = 5, now = new Date()) {
    const span = Number.isFinite(years) && years > 0 ? years : 5;
    return new Date(now.getFullYear() - span, now.getMonth(), now.getDate());
}

export function getExtractTexts(key) {
    if (key === "title") {
        return i18n.get(`${I18N_PREFIX}.extracts`);
    }
    return i18n.get(`${I18N_PREFIX}.extracts.${key}`);
}
